import { combineReducers, type Reducer } from "redux";
import {
  BEGIN_LOADING_NAVIGATION,
  CHANGE_NAVIGATION_MODE,
  CLOSE_NAVIGATION,
  GO_BACK,
  LOADING_SUCCESS,
  NAVIGATION_LOAD_SERIES_DATA,
  NAVIGATION_SERIES_TOGGLE_NODE,
  type NavigationActions,
  SELECT_NAV,
  SELECT_NAV_IMAGE,
} from "./actions";
import { type ImgData } from "../../utils/models";
import { type NavigationNode } from "../types";
import { type CategoryNavigationState, type NavigationRootState, type NavigationSeriesTreeState } from "./types";
import { arraysEqual, updateNodeInTree } from "../../Search/redux/reducer/seriesTreeReducer";
import { SearchDisplayMode } from "~/Search/models";

const isLoading: Reducer<boolean, NavigationActions> = (state = false, action) => {
  switch (action.type) {
    case BEGIN_LOADING_NAVIGATION: {
      return true;
    }
    case LOADING_SUCCESS: {
      return false;
    }

    default:
      return state;
  }
};

const EMPTY_ROOT: NavigationNode = {
  id: -1,
  gaLabel: "",
  title: "",
  level: -1,
  openInNewTab: false,
  children: [],
  usefulTools: [],
  showSeriesTree: false,
};

const INITIAL_CATEGORY_NAVIGATION_STATE: CategoryNavigationState = {
  root: EMPTY_ROOT,
  nodes: {},
};

const categoryNavigation: Reducer<CategoryNavigationState, NavigationActions> = (
  state = INITIAL_CATEGORY_NAVIGATION_STATE,
  action,
) => {
  switch (action.type) {
    case LOADING_SUCCESS: {
      return {
        root: action.payload.root,
        nodes: action.payload.nodes,
      };
    }

    default:
      return state;
  }
};

const flyoutNodes: Reducer<NavigationNode[], NavigationActions> = (state = [], action) => {
  switch (action.type) {
    case LOADING_SUCCESS: {
      return action.payload.flyoutNodes;
    }

    default:
      return state;
  }
};

const selectedNavIds: Reducer<number[], NavigationActions> = (state = [], action) => {
  switch (action.type) {
    case CLOSE_NAVIGATION: {
      return [];
    }

    case GO_BACK: {
      return state.slice(0, state.length - 1);
    }

    case SELECT_NAV: {
      const { nodeId, level } = action.payload;

      const selectedNodeId = state[level];
      if (nodeId === selectedNodeId) {
        return state.slice(0, level);
      }
      return state.slice(0, level).concat(nodeId);
    }

    default:
      return state;
  }
};

const selectedImage: Reducer<ImgData | null, NavigationActions> = (state = null, action) => {
  switch (action.type) {
    case CLOSE_NAVIGATION: {
      return null;
    }

    case SELECT_NAV_IMAGE: {
      return action.payload ?? null;
    }

    default:
      return state;
  }
};

const previousSelectedNavIds = (
  state: number[] = [],
  action: NavigationActions,
  previousSelectedNavIds: number[] = [],
): number[] => {
  switch (action.type) {
    case CLOSE_NAVIGATION: {
      return [];
    }

    case SELECT_NAV:
    case GO_BACK: {
      return previousSelectedNavIds;
    }

    default:
      return state;
  }
};

const seriesTree = (
  state: NavigationSeriesTreeState = {
    data: {
      state: "loading",
    },
    navigationMode: SearchDisplayMode.LIST,
  },
  action: NavigationActions,
): NavigationSeriesTreeState => {
  switch (action.type) {
    case SELECT_NAV:
    case CLOSE_NAVIGATION: {
      return {
        ...state,
        navigationMode: SearchDisplayMode.LIST,
      };
    }

    case CHANGE_NAVIGATION_MODE: {
      return {
        ...state,
        navigationMode: action.payload,
      };
    }
    case NAVIGATION_LOAD_SERIES_DATA: {
      return {
        ...state,
        data: {
          data: action.payload,
          state: "loaded",
        },
      };
    }
    case NAVIGATION_SERIES_TOGGLE_NODE: {
      if (!state.data.data) {
        return state;
      }
      const newTree = state.data.data.map((node) =>
        updateNodeInTree(node, { id: action.payload.nodeId, expanded: action.payload.isExpanded }),
      );
      if (arraysEqual(state.data.data, newTree)) {
        return state;
      }
      return {
        ...state,
        data: {
          ...state.data,
          data: newTree,
        },
      };
    }

    default:
      return state;
  }
};

export const navigationReducer: Reducer<NavigationRootState, NavigationActions> = (state, action) => {
  const updatedState = defaultNavigationReducer(state, action) as NavigationRootState;
  const updatedPreviousSelectedNavIds = previousSelectedNavIds(
    state?.previousSelectedNavIds,
    action,
    state?.selectedNavIds,
  );

  if (updatedPreviousSelectedNavIds !== state?.previousSelectedNavIds || updatedState !== state) {
    return {
      ...updatedState,
      previousSelectedNavIds: updatedPreviousSelectedNavIds,
    };
  } else {
    return state;
  }
};

const defaultNavigationReducer = combineReducers({
  isLoading,
  categoryNavigation,
  selectedNavIds,
  selectedImage,
  flyoutNodes,
  seriesTree,
});
